import { Checkbox, Col, Form, Radio, Row } from "antd"
import { useWatch } from "antd/lib/form/Form"
import cx from "clsx"
import {
  Alert,
  Button,
  CurrencyPercentageInput,
  Group,
  Image,
  Icon,
  ImageUpload,
  InputNumber,
  Legend,
  Modal,
  notification,
  Number,
  SelectInput,
  Space,
  Spinner,
  Text,
  TextInput,
  Title,
} from "components/common"
import { IconText, InstallmentGeneratorTable } from "components/custom"
import FeatureChargesTable from "components/custom/featureChargesTable/featureChargesTable"
import { useLazyGetLeadsQuery } from "features/clients/detail/api"
import { useLazyGetUnitsQuery } from "features/leads/api"
import { createFormLabels } from "features/projects/components/createFormLabels/createFormLabels"
import {
  useGenerateProposalMutation,
  useGetProjectsQuery,
  useLazyGetDesignationAndPaymentDiscountsQuery,
  useLazyGetPossessionAndInstalmentInfoQuery,
  useLazyGetProjectDetailQuery,
} from "features/shared/api"
import { useLazyGetLeadDetailsQuery } from "features/leads/api"
import { schemaRules } from "features/shared/utils"
import { useQueryUpdate } from "hooks"
import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { getCurrencySymbol, numberConvertor, roundValues } from "utils"
import getPrivilege from "utils/privileges/privileges"
import theme from "utils/themeVars"
import styles from "./index.module.less"
import PreviewProposalModal from "./previewProposalModal"
import getProposalValidationSchema from "./proposalGenerationValidationSchema"
import { InstallmentTypes } from "features/shared/data"
import MonthPicker from "components/common/monthPicker/monthPicker"
import DateTime from "utils/dateTime"

import Flex from "components/common/flex"

export default function ProposalGenerationForm({ formRef, from, inventory }) {
  const { t } = useTranslation()
  const { data: projectData, isFetching: isFetchingProjects } = useGetProjectsQuery({
    financials: true,
    paymentInfo: true,
  })

  const [isCustom, setIsCustom] = useState(false)
  const [getUnits, { data: units, isFetching: isFetchingUnits }] = useLazyGetUnitsQuery()

  const [getLeads, { data: leads, isFetching: isFetchingLeads, isErrorSearchLeads, errorSearchLead }] =
    useLazyGetLeadsQuery()
  const [getDesignationDiscounts, { data: designationDiscounts }] = useLazyGetDesignationAndPaymentDiscountsQuery()
  const [getPossessionAndInstalment, { data: possessAndIntalData, isFetching: isFetchingPossesionAndInstalments }] =
    useLazyGetPossessionAndInstalmentInfoQuery()
  const [generateProposal, { isLoading, error, reset: resetGenerateProposal, data }] = useGenerateProposalMutation()
  const [
    getProjectDetails,
    {
      data: projectDetail,
      isFetching: isFetchingProjectDetail,
      isLoading: isLoadingProjectDetail,
      error: errorProjectDetail,
    },
  ] = useLazyGetProjectDetailQuery()
  const [getLeadDetails, { data: leadSalesInfo, isFetching: isFetchingLeadDetails, isLoading: isLoadingDetails }] =
    useLazyGetLeadDetailsQuery()

  const [form] = Form.useForm()
  const pdfViewRef = useRef()
  const paymentPlanRef = useRef()
  const rules = schemaRules(getProposalValidationSchema())
  const { setFieldsValue } = form
  const projects = Form.useWatch("projects", form)
  const unit = Form.useWatch("unitNo", form)
  const paymentPlan = Form.useWatch("paymentPlanCheckbox", form)
  const unitFloorPlan = Form.useWatch("unitFloorPlanCheckbox", form)
  const uploadAttachment = Form.useWatch("uploadAttachments", form)
  const leadId = Form.useWatch("leadId", form)
  const clientsLeadId = useWatch("clientId", form)
  const months = useWatch("months", form)
  const discountPercentage = Form.useWatch("discountPercentage", form)
  const cdp = useWatch("cdp", form)
  const possessionAmount = useWatch("possessionAmount", form)
  const fixedFeaturesPrice = useWatch("featurePrice", form)
  const featureTable = useWatch("featureTable", form)
  const publishedPrice = useWatch("publishedPrice", form)
  const addBalloonPayment = useWatch("addBalloonPayment", form)
  const bookingPrice = useWatch("bookingPrice", form)
  const balloonPaymentAmount = useWatch("balloonPaymentAmount", form)
  const installmentFrequency = useWatch("installmentFrequency", form)
  const balloonFrequency = useWatch("balloonFrequency", form)
  const startDate = useWatch("startDate", form)
  const projectMedia = useWatch("mediaCheckbox", form)
  const instalmentType = useWatch("instalmentType", form)

  const getPossessionData = useCallback(
    ({ possessAndIntalData, currentCdp = null, currentBookingPrice = null }) => {
      const cdp = currentCdp !== null ? currentCdp : form.getFieldValue("cdp")
      const bookingPrice = currentBookingPrice !== null ? currentBookingPrice : form.getFieldValue("bookingPrice")
      const { possessionUnit, possessionPercentage, possession } = possessAndIntalData || {}
      const cdpValue = bookingPrice * cdp * 0.01
      const maxPossessionPercent = 100 - cdp
      const maxPossessionAmount = bookingPrice - cdpValue

      if (possessionUnit === "percentage") {
        const finalPossessionPercent = Math.min(possessionPercentage || 0, maxPossessionPercent)
        return {
          possessionPercentage: numberConvertor(finalPossessionPercent, "local", 2),
          possessionAmount: roundValues(bookingPrice * finalPossessionPercent * 0.01),
        }
      } else {
        const finalPossessionAmount = Math.min(possession || 0, maxPossessionAmount)
        return {
          possessionAmount: roundValues(finalPossessionAmount),
          possessionPercentage: numberConvertor((finalPossessionAmount / bookingPrice) * 100, "local", 2),
        }
      }
    },
    [form]
  )

  const currentPossessionPercentage = useMemo(() => {
    return getPossessionData({ possessAndIntalData }).possessionPercentage
  }, [possessionAmount])

  const getBalloonFrequency = useMemo(
    () =>
      [
        { label: t("Quarterly"), value: "quarterly", number: 3 },
        { label: t("BiAnnually"), value: "bi_annually", number: 6 },
        { label: t("Yearly"), value: "annually", number: 12 },
      ].filter(item => Math.floor(months / item?.number)),
    [months, t]
  )
  const isInTokenPDPOrCDPStage = leadSalesInfo => {
    return (
      leadSalesInfo?.contracts?.length > 0 &&
      (leadSalesInfo?.current === 3 || leadSalesInfo?.current === 2 || leadSalesInfo?.current === 1)
    )
  }
  const [updateQuery] = useQueryUpdate()
  const [formState, setFormState] = useState()
  const [installmentPlan, setInstallmentPlan] = useState([])

  const currency = useSelector(
    state =>
      state?.app?.user.info?.Agent?.AgencySettings?.find(currency => currency.key === "primary_currency")?.currencyCode
  )

  const columns = () => [
    { title: t("Feature"), dataIndex: "Feature" },
    { title: t("Amount"), dataIndex: "Amount" },
  ]

  let totalDealPrice = publishedPrice + fixedFeaturesPrice

  const calculateDownPayment = useMemo(() => bookingPrice * cdp * 0.01, [bookingPrice, cdp])
  let balloonFreqVal = [12, 6, 3]
  let balloonAmountValid = balloonFreqVal.some(value => !!Math.floor(months / value))

  const calculateTotalInstallment = useMemo(
    () =>
      bookingPrice -
      bookingPrice * cdp * 0.01 -
      bookingPrice * possessAndIntalData?.possessionPercentage * 0.01 -
      (balloonAmountValid ? (balloonPaymentAmount?.value ?? 0) : 0),

    [bookingPrice, cdp, possessAndIntalData, balloonPaymentAmount, balloonAmountValid]
  )

  const getFeatureTableData = useMemo(() => {
    return unit?.FeatureCharges?.map(item => {
      return {
        ...item,
        key: item?.featureId,
        Feature: item?.Feature?.name,
        Amount: (
          <>
            <Number
              value={item?.amount}
              isCurrency={!item?.amountType === "percentile"}
              wrapValue={val => (item?.amountType === "percentile" ? `${val} %` : val)}
            />
          </>
        ),
      }
    })
  }, [unit])

  const featureTableOnChange = (selectedRowKeys, selectedRows) => {
    let selectedFeaturePrices = 0
    let unitFeaturePrice = unit?.totalFixedFeatureCharges

    selectedRows?.forEach(item => {
      selectedFeaturePrices += item?.amountType === "fixed" ? +item?.amount : (+item?.amount / 100) * publishedPrice
    })
    let totalPrice = publishedPrice + unitFeaturePrice + selectedFeaturePrices
    form.setFieldValue("featureTable", {
      selectedRowKeys,
      selectedPrices: selectedFeaturePrices,
      features: selectedRows,
    })
    form.setFieldsValue({
      featurePrice: unitFeaturePrice + selectedFeaturePrices,
      bookingPrice: totalPrice,
      possessionAmount: getPossessionData({ possessAndIntalData,currentBookingPrice:totalPrice }).possessionAmount,
      discountPercentage: 0,
    })
  }

  const paymentPlanExist = installmentPlan?.length === 0
  const maxDiscountBasedOnCdp = useCallback(
    totalDealPrice => {
      // designation discounts are sorted in descending order based on payments
      let paymentArray = designationDiscounts?.PaymentDiscounts
      let cdpAmount = +cdp || 0
      let maxDesignationAmount = 0
      let maxDesignationDiscount = 0
      const minPayment = designationDiscounts?.PaymentDiscounts?.[paymentArray?.length - 1]?.payment

      designationDiscounts?.maxDesignationAmountArray?.forEach(item => {
        maxDesignationAmount = Math.max(maxDesignationAmount, (+item?.discount / totalDealPrice) * 100)
      })
      maxDesignationDiscount = Math.max(maxDesignationAmount, designationDiscounts?.maxDesignationDiscountPercentage)

      if (!designationDiscounts?.PaymentDiscounts?.length || cdpAmount < minPayment || cdpAmount <= 0 || !cdpAmount)
        return maxDesignationDiscount

      //Applying binary search to find the next great payment, we won't cater for the edge case for value being lesser then minPayment
      let min = 0
      let max = (+designationDiscounts?.PaymentDiscounts?.length ?? 1) - 1
      let ans = 0

      while (min <= max) {
        let mid = Math.ceil(min + (max - min) / 2)
        if (paymentArray?.[mid]?.payment <= cdpAmount) {
          max = mid - 1
          ans =
            paymentArray[mid]?.discountType === "percentage"
              ? paymentArray[mid]?.discount
              : (paymentArray[mid]?.discount / totalDealPrice) * 100
        }
        if (paymentArray?.[mid]?.payment > cdpAmount) {
          min = mid + 1
        }
      }
      return Math.max(ans, +maxDesignationDiscount).toFixed(2)
    },
    [designationDiscounts, cdp]
  )

  const updateUnitPricingAndDiscounts = useCallback(
    useProposalUnits => {
      const unit = form.getFieldValue("unitNo")
      const projects = form.getFieldValue("projects")
      if (!!unit?.id) {
        form.setFieldsValue({
          unitType: unit?.Type?.name,
          discountPercentage: 0,
          bookingPrice: unit?.unitPrice + unit?.totalFixedFeatureCharges,
          publishedPrice: unit?.unitPrice,
          featurePrice: unit?.totalFixedFeatureCharges,
          cdp: useProposalUnits
            ? unit?.ContractListings?.[0]?.cdpCommitment || 0
            : +projects?.ProjectFinancial?.completeDownPayment || 0,
        })

        getDesignationDiscounts({ projectId: projects?.id, listingId: unit?.id }, true)

        getPossessionAndInstalment(
          {
            projectId: projects?.id,
            listingId: unit?.id,
            dealPrice: unit?.unitPrice,
          },
          true
        )
          .unwrap()
          .then(data => {
            form.setFieldsValue({
              possessionAmount: getPossessionData({ possessAndIntalData: data }).possessionAmount,
              paymentPlanCheckbox: data?.isInstalmentsRequired,
              instalmentType: data?.instalmentType?.length === 2 ? "time_based" : data?.instalmentType?.[0],
            })
          })
      }
    },
    [from, leadSalesInfo]
  )

  const onModalVisible = useCallback(() => {
    if (from?.name === "lead") {
      getLeadDetails({ leadId: from?.listingId })
        .unwrap()
        .then(leadSalesInfo => {
          if (isInTokenPDPOrCDPStage(leadSalesInfo)) {
            let projectObj = projectData?.find(item => item?.projectId === leadSalesInfo?.projectId)
            form.setFieldsValue({ projects: projectObj })
            getProjectDetails({ id: projectObj?.projectId })
            getUnits({ projectId: projectObj.id, module: "proposal" })
              .unwrap()
              .then(data => {
                let unitsObj = data?.find(item => item?.value === leadSalesInfo?.unitId)
                form.setFieldsValue({ unitNo: unitsObj })
                updateUnitPricingAndDiscounts(true)
              })
          }
        })
    } else if (from?.name === "inventory" || from?.name === "project") {
      let projectObj = projectData?.find(item => item?.projectId === from?.projectId)
      form.setFieldsValue({ projects: projectObj })
      getProjectDetails({ id: projectObj?.projectId })
      if (from?.name === "inventory") {
        getUnits({ projectId: from?.projectId, module: "proposal" })
          .unwrap()
          .then(data => {
            let unitsObj = data?.find(item => item?.value === from?.listingId)
            setFieldsValue({ unitNo: unitsObj })
            updateUnitPricingAndDiscounts(false)
          })
      }
    }
  }, [from, projectData, leadSalesInfo])

  const onSubmit = async values => {
    const response = await generateProposal({
      listingId: unit?.listingId,
      inquiryId: leadId || from?.listingId,
      payload: values,
      floorPlan: uploadAttachment,
      preview: true,
      isCustom: isCustom,
      customPaymentPlan: installmentPlan,
      selectPaymentPlan: paymentPlan,
    })
      .unwrap()
      .then(url => {
        setFormState({
          ...values,
          floorPlan: uploadAttachment,
          listingId: unit?.listingId,
          inquiryId: leadId || from?.listingId,
          imageData: url,
          paymentPlan: paymentPlan,
          isCustom: isCustom,
        })
        pdfViewRef?.current?.setVisible(true)
        formRef?.current?.setVisible(false)
      })
      .catch(error => {
        notification.error({ message: error?.message })
      })

    if (!response?.error) {
      resetGenerateProposal()
    }
  }

  const onSearch = useCallback(() => {
    form.setFieldsValue({ clientId: null })
    getLeads({ id: leadId })
    getLeadDetails({ leadId: leadId })
      .unwrap()
      .then(leadSalesInfo => {
        if (isInTokenPDPOrCDPStage(leadSalesInfo)) {
          if (from?.name === "project" && leadSalesInfo?.projectId === projects?.id) {
            getUnits({ projectId: projects?.id, module: "proposal" })
              .unwrap()
              .then(data => {
                let unitsObj = data?.find(item => item?.value === leadSalesInfo?.unitId)
                form.setFieldsValue({ unitNo: unitsObj })
                updateUnitPricingAndDiscounts(true)
              })
          } else if (
            from?.name === "inventory" &&
            leadSalesInfo?.projectId === projects?.id &&
            leadSalesInfo?.unitId === unit?.id
          ) {
            updateUnitPricingAndDiscounts(true)
          }
        } else {
          getUnits({ projectId: projects?.id })
        }
      })
  }, [leadId])
  const instalmentData = possessAndIntalData?.instalmentMap?.[instalmentType]
  const monthOptions = useMemo(() => {
    const monthData = []

    for (let i = 1; i <= instalmentData?.instalments?.[0]?.instalment; i++) {
      monthData.push({ label: i, value: i })
    }

    return monthData
  }, [instalmentData])

  const installmentTypeOptions = useMemo(() => {
    let data = [
      { label: t("Monthly"), value: "monthly" },
      +months % 3 === 0 ? { label: t("Quarterly"), value: "quarterly" } : null,
      +months % 6 === 0 ? { label: t("BiAnnually"), value: "bi_annually" } : null,
    ].filter(option => option !== null)

    return data
  }, [months])

  const onHandleProjectChange = (value, option) => {
    setFieldsValue({
      projects: option,
      unitNo: null,
      discountPercentage: 0,
      cdp: option?.ProjectFinancial?.completeDownPayment,
      bookingPrice: null,
      publishedPrice: null,
      featurePrice: null,
      possessionAmount: null,
    })
    getUnits({ projectId: option?.projectId })
    getProjectDetails({ id: option?.projectId })
  }

  const onHandleUnitNoChange = (value, option) => {
    setFieldsValue({
      unitNo: option,
      discountPercentage: 0,
      bookingPrice: roundValues(option?.unitPrice + option?.totalFixedFeatureCharges),
      publishedPrice: roundValues(option?.unitPrice),
      featurePrice: roundValues(option?.totalFixedFeatureCharges),
      cdp: +projects?.ProjectFinancial?.completeDownPayment || 0,
      unitType: option?.Type?.name,
    })
    getDesignationDiscounts({ projectId: projects?.projectId, listingId: option?.listingId }, true)

    getPossessionAndInstalment(
      { projectId: projects?.projectId, listingId: option?.listingId, dealPrice: option?.unitPrice },
      true
    )
      .unwrap()
      .then(data => {
        setFieldsValue({
          possessionAmount: getPossessionData({ possessAndIntalData: data }).possessionAmount,
          paymentPlanCheckbox: data?.isInstalmentsRequired,
          instalmentType: data?.instalmentType?.length === 2 ? "time_based" : data?.instalmentType?.[0],
        })
      })
  }

  const isUpfrontPaymentComplete = possessionAmount + bookingPrice * cdp * 0.01 === bookingPrice
  const disablePaymentPlans = useMemo(() => {
    let disablePaymentPlanCheckBox = isUpfrontPaymentComplete || possessAndIntalData?.isInstalmentsRequired === false
    if (disablePaymentPlanCheckBox) {
      form.setFieldValue("paymentPlanCheckbox", false)
    }
    return disablePaymentPlanCheckBox
  }, [isUpfrontPaymentComplete, possessAndIntalData])

  const showMessage = message => (
    <IconText
      icon="BsInfo"
      title={message}
      iconProps={{
        style: { color: "#707070" },
        hasBackground: true,
        size: "1.3em",
        iconContainerStyle: { horizontalAlign: 1, padding: 0, "--icon-bg-color": "#E6E6E6" },
      }}
      style={{ "--typography-color": "#9D9D9D", fontSize: 12, gap: 8, marginBottom: 13 }}
    />
  )

  const onHandleDiscPercChange = value => {
    let bookingPrice = totalDealPrice - +value * 0.01 * (totalDealPrice || 0)
    form.setFieldsValue({
      discountPercentage: value || 0,
      bookingPrice: roundValues(bookingPrice),
      possessionAmount: getPossessionData({ possessAndIntalData, currentBookingPrice: bookingPrice }).possessionAmount,
    })
  }

  const isAllowedByPrivilege = value =>
    !getPrivilege("can_generate_proposal", leads?.data?.find(item => item?.id === value)?.privileges)

  const renderMedia = e => {
    return <Image height={75} width={85} src={e?.src} alt="media" />
  }
  const handleCdpChange = val => {
    form.setFieldsValue({
      cdp: val,
      possessionAmount: getPossessionData({ possessAndIntalData, currentCdp: val }).possessionAmount,
    })
  }

  const floorPlans = projectDetail?.Images?.floorPlans

  const hasFloorPlans = Array.isArray(floorPlans)
    ? floorPlans.length > 0
    : floorPlans && typeof floorPlans === "object"
      ? Object.values(floorPlans).some(value => Array.isArray(value) && value.length > 0)
      : false

  const disableProjectOrInventoryButton = useMemo(() => {
    if (from?.name === "lead" || from?.name === "project") {
      return !!isInTokenPDPOrCDPStage(leadSalesInfo)
    }
  }, [leadSalesInfo, from])

  const errorForSales = useMemo(() => {
    switch (from?.name) {
      case "project":
        if (isInTokenPDPOrCDPStage(leadSalesInfo) && projects?.id !== leadSalesInfo?.projectId)
          return { message: t("Enter the correct Lead ID for the project against which the sale has been added") }
        break

      case "inventory":
        if (
          isInTokenPDPOrCDPStage(leadSalesInfo) &&
          (projects?.id !== leadSalesInfo?.projectId || leadSalesInfo?.unitId !== unit?.id)
        )
          return { message: t("Enter the correct Lead ID for the unit against which the sale has been added") }
        break

      default:
        return null
    }
    return null
  }, [leadSalesInfo, projects, unit, from])

  return (
    <div>
      <Modal
        maskClosable={false}
        title={t("Project Proposal")}
        ref={formRef}
        okText={t("Generate")}
        disableOkButton={(!clientsLeadId && from?.name !== "lead") || errorForSales !== null}
        width={880}
        cancelText={t("Cancel")}
        loading={isLoading}
        onOk={form.submit}
        footerAlert={error || errorForSales}
        destroyOnClose={true}
        scrollToFirstError
        onModalVisible={onModalVisible}
        bodyStyle={!projects || !unit ? { overflow: "visible" } : null}
        onCancel={() => {
          resetGenerateProposal()
          setInstallmentPlan([])
          formRef?.current?.setVisible(false)
          if (from?.name !== "lead") {
            updateQuery("getLeads", draft => null)
          }
          updateQuery("getLeadDetails", draft => null)
          updateQuery(
            "getUnits",
            draft => null,
            () => {},
            { projectId: projects?.id, module: "proposal" }
          )
          form.resetFields()
        }}
      >
        {isFetchingProjects ? (
          <Spinner inDisabledLayout />
        ) : (
          <Form
            form={form}
            layout="vertical"
            name="proposalForm"
            scrollToFirstError
            preserve={true}
            onFinish={onSubmit}
            disabled={isFetchingProjects}
            colon={false}
          >
            <Legend title={t("Project & Lead Details")} />

            {from?.name !== "lead" && (
              <>
                <Group template="315px 1fr" gap="6px 24px" style={{ alignItems: "flex-start" }}>
                  <Form.Item
                    name="leadId"
                    label={t("Lead ID")}
                    required
                    validateStatus={isErrorSearchLeads ? "error" : undefined}
                    rules={rules}
                  >
                    <TextInput
                      value={leadId}
                      placeholder={t("Search By Lead ID")}
                      onChange={e => form.setFieldsValue({ leadId: e.target.value })}
                    />
                    <div>
                      {(isErrorSearchLeads || leads?.data?.length === 0) && (
                        <IconText
                          icon="MdInfoOutline"
                          iconProps={{ color: "red", size: "1em" }}
                          textType={"danger"}
                          title={
                            isErrorSearchLeads
                              ? errorSearchLead?.message
                              : leads?.data?.length === 0
                                ? t("Leads Not Found")
                                : ""
                          }
                        />
                      )}
                    </div>
                  </Form.Item>
                  <div style={{ top: "30px", position: "relative" }}>
                    <Button
                      loading={isFetchingLeads}
                      type="primary"
                      size="large"
                      onClick={onSearch}
                      icon="FiSearch"
                      text={t("Search")}
                      style={{ height: 48 }}
                      disabled={leadId ? false : true}
                    />
                  </div>
                </Group>
                <Form.Item
                  name="clientId"
                  hidden={!leads?.data?.length || !!inventory?.leadId}
                  getValueFromEvent={e => {
                    if (isAllowedByPrivilege(e.target.value)) {
                      notification.error({
                        message: t("You do not have privilege to create proposal for this lead"),
                      })
                    } else return e.target.value
                  }}
                >
                  {!!inventory?.leadId ? (
                    setFieldsValue({ clientId: inventory?.leadId })
                  ) : (
                    <Radio.Group className="w-100" value={clientsLeadId}>
                      <Group template="repeat(3,1fr)" gap="16px">
                        {!!leads?.data?.length &&
                          leads?.data?.slice(0, 3).map(e => (
                            <Radio className={styles.cardRadio} value={e?.id} key={e?.id}>
                              <Text type="secondaryLight" block>
                                ID: {e?.id}
                              </Text>
                              <Text>{e?.clientName}</Text>
                            </Radio>
                          ))}
                      </Group>
                    </Radio.Group>
                  )}
                </Form.Item>
              </>
            )}

            <Group template="repeat(3, 1fr)" gap="6px 32px">
              <Form.Item name="projects" label={t("Select Project")} required rules={rules}>
                <SelectInput
                  placeholder={t("Select Project")}
                  options={projectData || []}
                  disabled={from?.name !== "lead" || !paymentPlanExist || disableProjectOrInventoryButton}
                  onChange={onHandleProjectChange}
                  getPopupContainer={true}
                />
              </Form.Item>
              <Form.Item name="unitNo" label={t("Unit No")} required rules={rules}>
                <SelectInput
                  inputLoading={isFetchingUnits}
                  placeholder={t("Select Unit No")}
                  options={units || []}
                  onChange={onHandleUnitNoChange}
                  disabled={
                    ((!clientsLeadId || !leadId || from?.name === "inventory") && from?.name !== "lead") ||
                    !paymentPlanExist ||
                    disableProjectOrInventoryButton
                  }
                  getPopupContainer={true}
                />
              </Form.Item>

              <Form.Item name="unitType" label={t("Unit Type")}>
                <TextInput placeholder={t("Unit Type")} disabled />
              </Form.Item>
            </Group>

            {projects && unit && (from?.name !== "lead" ? !!clientsLeadId : true) && errorForSales === null && (
              <>
                <Legend title={t("Payment Details")} />
                <Group template="repeat(2, 1fr)" gap="6px 32px">
                  <Form.Item name="publishedPrice" label={t("Published Price")}>
                    <TextInput
                      loading={isFetchingPossesionAndInstalments}
                      placeholder={t("Published price")}
                      suffixText={getCurrencySymbol()}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item name="featurePrice" label={t("Features Price")}>
                    <TextInput
                      loading={isFetchingPossesionAndInstalments}
                      placeholder={t("Feature price")}
                      suffixText={getCurrencySymbol()}
                      value={fixedFeaturesPrice}
                      disabled
                    />
                    <div>
                      <IconText
                        icon="MdInfoOutline"
                        iconProps={{ color: theme["gray800"], size: "1em" }}
                        textType="secondary"
                        title={t("Total cost of all selected features")}
                      />
                    </div>
                  </Form.Item>
                </Group>
                {unit?.FeatureCharges?.length !== 0 && (
                  <Form.Item name={"featureTable"} label="Features" labelCol={{ span: 6 }} wrapperCol={{ span: 25 }}>
                    <FeatureChargesTable
                      disabled={!paymentPlanExist}
                      columns={columns()}
                      tableData={getFeatureTableData}
                      onChange={featureTableOnChange}
                      selectedRowKeys={featureTable?.selectedRowKeys}
                    />
                  </Form.Item>
                )}
                <Group template="repeat(2, 1fr)" gap="6px 32px">
                  <Form.Item
                    name="cdp"
                    label={t("Complete Down Payment")}
                    rules={rules}
                    required
                    style={{ marginBottom: "32px" }}
                  >
                    <InputNumber
                      disabled={!paymentPlanExist}
                      value={cdp}
                      placeholder={t("Please Add CDP")}
                      style={{ width: "100%" }}
                      min={
                        unit?.ContractListings?.[0]?.cdpCommitment || +projects?.ProjectFinancial?.completeDownPayment
                      }
                      max={100}
                      control={false}
                      onChange={handleCdpChange}
                      addonAfter={<Number value={cdp ? +cdp * 0.01 * bookingPrice : 0} tooltip isCurrency />}
                    />
                  </Form.Item>
                  <Form.Item name="discountPercentage" label={t("Discount Applied")} rules={rules} required>
                    <InputNumber
                      precision={3}
                      value={discountPercentage}
                      style={{ width: "100%" }}
                      placeholder={t("Please add discount percentage")}
                      max={maxDiscountBasedOnCdp(totalDealPrice) || 0}
                      min={0}
                      controls={false}
                      addonAfter={<Number value={totalDealPrice * discountPercentage * 0.01} tooltip isCurrency />}
                      onChange={onHandleDiscPercChange}
                      disabled={!cdp || !paymentPlanExist}
                    />
                    <div>
                      <IconText
                        icon="MdInfoOutline"
                        iconProps={{ color: theme["gray800"], size: "1em" }}
                        textType="secondary"
                        title={`${t("Maximum Discount")}: ${maxDiscountBasedOnCdp(totalDealPrice) || 0}%`}
                      />
                    </div>
                  </Form.Item>

                  <Form.Item name="bookingPrice" label={t("Booking Price")} required rules={rules}>
                    <TextInput
                      loading={isFetchingPossesionAndInstalments}
                      placeholder={t("Booking price")}
                      suffixText={getCurrencySymbol()}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item name="possessionAmount" label={t("Possession Amount")} rules={rules} required>
                    <TextInput
                      value={possessionAmount}
                      loading={isFetchingPossesionAndInstalments}
                      placeholder={t("Possession amount")}
                      suffixText={getCurrencySymbol()}
                      disabled
                    />
                    <IconText
                      icon="MdInfoOutline"
                      iconProps={{ color: theme["gray800"], size: "1em" }}
                      textType="secondary"
                      title={`${currentPossessionPercentage}% ${t("of Deal Price")}`}
                    />
                  </Form.Item>
                </Group>
                <Form.Item
                  className={cx("span-all", styles.leadDetailForm)}
                  name="paymentPlanCheckbox"
                  label=""
                  valuePropName="checked"
                >
                  <Checkbox
                    disabled={disablePaymentPlans}
                    defaultChecked={possessAndIntalData?.isInstalmentsRequired}
                    onChange={e => form.setFieldValue("paymentPlanCheckbox", e.target.checked)}
                  >
                    <Title className="mbe-0" level={4}>
                      {t("Payment Plan")}
                    </Title>
                  </Checkbox>
                </Form.Item>
                {isUpfrontPaymentComplete
                  ? showMessage(t("Installment plan is not applicable due to 100% upfront payment"))
                  : possessAndIntalData?.isInstalmentsRequired === false
                    ? showMessage(`${t("Installment plans are not applicable on")} ${projects?.name}`)
                    : null}{" "}
                {paymentPlan && (
                  <>
                    <Group template="repeat(2, 1fr)" gap="6px 32px">
                      <Form.Item
                        name="instalmentType"
                        label={t("Payment Plan Type")}
                        help={instalmentType == "construction_based" && isCustom ? t("Custom") : null}
                      >
                        <SelectInput
                          placeholder={t("Select Plan Type")}
                          disabled={possessAndIntalData?.disableInstalmentType}
                          defaultValue={instalmentType}
                          options={InstallmentTypes()}
                        />
                      </Form.Item>
                      {instalmentType == "time_based" && (
                        <>
                          <Form.Item name="startDate" label={t("Start Date")} required rules={rules}>
                            <MonthPicker
                              allowClear={false}
                              showTime={false}
                              format={"MMMM YYYY"}
                              picker={"month"}
                              containerWidth="100%"
                              disabled={!paymentPlanExist}
                              noCurrentMonth
                              disabledDate={date => date && date < DateTime.getDateAndTime(new Date()).startOf("month")}
                            />
                          </Form.Item>

                          <Form.Item
                            name="months"
                            label={t("Instalment Duration")}
                            required
                            rules={rules}
                            help={`${t("Maximum Instalments Duration ")}: ${
                              instalmentData?.instalments?.[0]?.instalment || 0
                            } ${t("Months")}`}
                          >
                            <SelectInput
                              placeholder={t("Select months")}
                              options={monthOptions}
                              allowClear
                              onChange={value => {
                                setFieldsValue({
                                  months: value,
                                  addBalloonPayment: false,
                                  balloonFrequency: null,
                                })

                                form.resetFields(["installmentFrequency"])
                              }}
                              disabled={!paymentPlanExist}
                            />
                          </Form.Item>

                          <Form.Item
                            name="installmentFrequency"
                            label={t("Instalment Frequency")}
                            required
                            rules={rules}
                            help={isCustom ? t("Custom") : null}
                          >
                            <SelectInput
                              options={installmentTypeOptions}
                              placeholder={t("Select Instalment Frequency")}
                              allowClear
                              disabled={!paymentPlanExist}
                            />
                          </Form.Item>
                        </>
                      )}
                    </Group>

                    {instalmentType == "time_based" && (
                      <Form.Item name="addBalloonPayment" required rules={rules} valuePropName="checked">
                        <Checkbox
                          onChange={e =>
                            form.setFieldsValue({
                              addBalloonPayment: e.target.checked,
                              balloonPaymentAmount: {
                                value: null,
                                percentage: null,
                                inputType: "%",
                              },
                            })
                          }
                          disabled={!paymentPlanExist || !balloonAmountValid}
                        >
                          {t("Add Balloon Payment")}
                        </Checkbox>
                      </Form.Item>
                    )}
                    <Group template="repeat(2, 1fr)" gap="6px 32px">
                      {addBalloonPayment ? (
                        <>
                          <Form.Item
                            name={"balloonPaymentAmount"}
                            label={t("Total Balloon Amount")}
                            required
                            rules={rules}
                          >
                            <CurrencyPercentageInput
                              totalPrice={bookingPrice}
                              field={balloonPaymentAmount}
                              placeholder={t("Enter Total Balloon Payment")}
                              maxAmount={
                                bookingPrice -
                                bookingPrice * cdp * 0.01 -
                                bookingPrice * possessAndIntalData?.possessionPercentage * 0.01
                              }
                              minAmount={1}
                              minPercentage={5e-324}
                              onChange={option => {
                                form.setFieldsValue({
                                  balloonPaymentAmount: { ...option, value: option?.value },
                                })
                              }}
                              maxPercentage={100 - cdp - possessAndIntalData?.possessionPercentage}
                              disabled={!paymentPlanExist}
                            />
                          </Form.Item>
                          <Form.Item
                            name={"balloonFrequency"}
                            label={t("Frequency")}
                            required
                            rules={rules}
                            help={isCustom ? t("Custom") : <></>}
                          >
                            <SelectInput
                              placeholder={t("Select Frequency")}
                              options={getBalloonFrequency}
                              disabled={!paymentPlanExist}
                            />
                          </Form.Item>
                        </>
                      ) : (
                        <></>
                      )}
                      <Space size={8} align="end" style={{ marginBottom: 18 }}>
                        <Button
                          text={t("Edit Plan")}
                          icon="TbEdit"
                          size="small"
                          type="light"
                          color={theme["primary-color"]}
                          className="pi-12"
                          style={{ border: "1px solid currentColor", height: 46 }}
                          onClick={() => paymentPlanRef?.current?.setVisible(true)}
                          disabled={
                            !paymentPlan ||
                            !!installmentPlan?.length ||
                            (instalmentType == "time_based" && !(months && installmentFrequency)) ||
                            (instalmentType == "construction_based" && !possessAndIntalData?.instalment?.length) ||
                            (addBalloonPayment && !(balloonPaymentAmount?.value && balloonFrequency))
                          }
                        />
                        {
                          <Button
                            icon="FiTrash2"
                            size="small"
                            type="light"
                            color={theme["error-color"]}
                            className="pi-12"
                            style={{
                              border: "1px solid currentColor",
                              height: 46,
                              width: 46,
                            }}
                            disabled={paymentPlanExist}
                            onClick={() => {
                              notification.success({ message: t("Your Payment Plan is successfully deleted") })
                              setInstallmentPlan([])
                              setIsCustom(false)
                            }}
                          />
                        }
                      </Space>
                    </Group>
                  </>
                )}
                <Form.Item
                  className={cx("span-all", styles.leadDetailForm)}
                  name="mediaCheckbox"
                  label=""
                  valuePropName="checked"
                >
                  <Checkbox onChange={e => form.setFieldValue("mediaCheckbox", e.target.checked)}>
                    <Title className="mbe-0" level={4}>
                      {t("Project Media And Cover Page")}
                    </Title>
                  </Checkbox>
                </Form.Item>
                {projectMedia && (
                  <>
                    {projectDetail?.Images?.coverPage?.length > 0 && (
                      <Form.Item
                        layout="horizontal"
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        labelAlign="left"
                        name="selectedCoverPhotos"
                        label={createFormLabels({
                          fieldName: t("Select Proposal Cover Page"),
                          helpText: t("Select max 1 files"),
                        })}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value && value.length > 4) {
                                return Promise.reject(new Error(t("You can select only one cover photo")))
                              }
                              return Promise.resolve()
                            },
                          },
                        ]}
                      >
                        <Checkbox.Group>
                          <Flex align="start" gap="8px">
                            {projectDetail?.Images?.coverPage?.map((e, index) => (
                              <div key={index} style={{ position: "relative", display: "inline-block" }}>
                                <Checkbox value={e} className={styles.checkboxStyle} />
                                {renderMedia(e)}
                              </div>
                            ))}
                          </Flex>
                        </Checkbox.Group>
                      </Form.Item>
                    )}

                    {projectDetail?.Images?.projectPhotos?.length > 0 && (
                      <Form.Item
                        layout="horizontal"
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        labelAlign="left"
                        name="selectedProjectPhotos"
                        label={createFormLabels({
                          fieldName: t("Select Project Photos"),
                          helpText: t("Select max 4 files"),
                        })}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value && value.length > 4) {
                                return Promise.reject(new Error(t("You can select up to 4 project photos only")))
                              }
                              return Promise.resolve()
                            },
                          },
                        ]}
                      >
                        <Checkbox.Group>
                          <Flex align="start" gap="8px">
                            {projectDetail?.Images?.projectPhotos?.map((e, index) => (
                              <div key={index} style={{ position: "relative", display: "inline-block" }}>
                                <Checkbox value={e} className={styles.checkboxStyle} />

                                {renderMedia(e)}
                              </div>
                            ))}
                          </Flex>
                        </Checkbox.Group>
                      </Form.Item>
                    )}

                    {hasFloorPlans && (
                      <Form.Item
                        label={createFormLabels({
                          fieldName: t("Select Floor Plan"),
                          helpText: t("Select max 2 files"),
                        })}
                        layout="horizontal"
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        labelAlign="left"
                      >
                        <Form.Item
                          name="selectedFloorPlans"
                          className={styles.formFieldHeight}
                          rules={[
                            {
                              validator: (_, value = []) => {
                                if (value.length > 2) {
                                  return Promise.reject(new Error(t("You can select up to 2 floor plans only")))
                                }
                                const types = value?.map(item => item.mediaType)
                                const hasDuplicateTypes = types?.length !== new Set(types).size
                                if (hasDuplicateTypes) {
                                  return Promise.reject(new Error(t("You can select only one floor plan per type")))
                                }
                                return Promise.resolve()
                              },
                            },
                          ]}
                        >
                          <Checkbox.Group style={{ flexDirection: "column", gap: 12, width: "100%" }}>
                            {Object.entries(projectDetail?.Images?.floorPlans).map(
                              ([key, value]) =>
                                value?.length > 0 && (
                                  <Flex gap="4px" align="normal" vertical>
                                    <span className="semiBold">
                                      <Text type="secondary" className="semiBold">
                                        {key}
                                      </Text>
                                    </span>
                                    <Space size={12} wrap>
                                      {value.map((item, index) => (
                                        <div style={{ position: "relative", display: "inline-block" }}>
                                          <Checkbox
                                            key={item.id || index}
                                            value={item}
                                            className={styles.checkboxStyle}
                                          />
                                          {renderMedia(item)}
                                        </div>
                                      ))}
                                    </Space>
                                  </Flex>
                                )
                            )}
                          </Checkbox.Group>
                        </Form.Item>
                      </Form.Item>
                    )}
                  </>
                )}
                <Form.Item
                  className={cx("span-all", styles.leadDetailForm)}
                  name="unitFloorPlanCheckbox"
                  valuePropName="checked"
                >
                  <Checkbox onChange={e => form.setFieldValue("unitFloorPlanCheckbox", e.target.checked)}>
                    <Title className="mbe-0" level={4}>
                      {t("Custom Floor Plan")}
                    </Title>
                  </Checkbox>
                </Form.Item>
                {unitFloorPlan && (
                  <Form.Item
                    layout="horizontal"
                    labelCol={{ span: 5 }}
                    wrapperCol={{ span: 19 }}
                    labelAlign="left"
                    name="uploadAttachments"
                    label={t("Attachments")}
                    required={unitFloorPlan}
                    dependencies={[unitFloorPlan]}
                    rules={rules.concat([
                      () => ({
                        validator(_, value) {
                          if (value?.fileList?.length == 0 && unitFloorPlan) {
                            return Promise.reject(new Error(t("Attachment is required")))
                          }
                          return Promise.resolve()
                        },
                      }),
                    ])}
                  >
                    <ImageUpload
                      withDimensionLimit={false}
                      picture={true}
                      noCard={true}
                      setFiles={data => form.setFieldValue("uploadAttachment", data)}
                      files={uploadAttachment?.fileList}
                      className={styles.uploadID}
                      uploadDescription={t("JPG, or PNG file format up till 1 MB")}
                      limit={3}
                      mode="multiple"
                    />
                  </Form.Item>
                )}
                <div>
                  <Legend title={t("Proposal Settings")} />
                  <Form.Item
                    label={t("Please tick the checkbox below to customize your proposal")}
                    name={"proposalSettings"}
                    initialValue={[
                      "showClientName",
                      "showUnitNetArea",
                      "showUnitGrossArea",
                      "showPossessionAmount",
                      "includeBusinessCard",
                    ]}
                    required
                  >
                    <Checkbox.Group
                      className={styles.checkboxGroup}
                      style={{
                        width: "100%",
                      }}
                      onChange={list => {
                        form.setFieldsValue({ proposalSettings: list })
                      }}
                    >
                      <Checkbox value="showUnitNetArea">
                        <Text type="secondary">{t("Show Unit Net Area")}</Text>
                      </Checkbox>

                      <Checkbox value="showUnitGrossArea">
                        <Text type="secondary">{t("Show Unit Gross Area")}</Text>
                      </Checkbox>

                      <Checkbox value="showPossessionAmount">
                        <Text type="secondary">{t("Show Possession Amount")}</Text>
                      </Checkbox>

                      <Checkbox value="generateBookingForm">
                        <Text type="secondary">{t("Generate Booking Form PDF")}</Text>
                      </Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                  <IconText
                    icon="MdInfoOutline"
                    iconProps={{ color: theme["gray800"], size: "1em" }}
                    textType="secondary"
                    title={t("A task of proposal will be added on the selected lead")}
                  />
                </div>
              </>
            )}
          </Form>
        )}
      </Modal>

      <InstallmentGeneratorTable
        setIsCustom={setIsCustom}
        startDate={startDate}
        modalRef={paymentPlanRef}
        paymentInstallments={installmentPlan}
        onPaymentInstallmentsChange={setInstallmentPlan}
        totalInstallment={calculateTotalInstallment}
        totalBalloonPayment={balloonAmountValid ? +balloonPaymentAmount?.value : 0}
        frequencyTypeInstallment={installmentFrequency}
        frequencyTypeBalloon={balloonFrequency}
        downPayment={calculateDownPayment}
        possession={+possessionAmount}
        installmentMonths={months}
        isBallonPaymentRequired={addBalloonPayment}
        paymentData={{
          bookingPrice: bookingPrice,
          downPayment: calculateDownPayment,
          possessionCharges: possessionAmount,
        }}
        constructionBasedPlan={instalmentData?.instalments}
        instalmentType={instalmentType}
      />
      <PreviewProposalModal
        isCustom={isCustom}
        customPaymentPlan={installmentPlan}
        pdfViewRef={pdfViewRef}
        formRef={formRef}
        formState={formState}
        from={from}
        form={form}
        isLoading={isLoading}
        error={error}
        generateProposal={generateProposal}
        resetGenerateProposal={resetGenerateProposal}
      />
    </div>
  )
}
